/**
 * Transitional states during which update/edit operations should be blocked.
 * These states indicate an operation is already in progress for the service.
 */
const TRANSITIONAL_STATES = new Set([
  'creating',
  'deploying',
  'updating',
  'suspending',
  'resuming',
  'deleting',
]);

/**
 * Terminal failure states for a cluster. In these states the backend rejects
 * resume/suspend, so the UI hides those actions entirely. Both 'error' (the
 * QueryServiceStatus union value) and 'failed' are matched, case-insensitively,
 * so gating works regardless of which value the backend reports.
 */
const FAILED_STATES = new Set(['error', 'failed']);

/**
 * Check if a service status is a transitional state where updates should be blocked.
 * Works for both QueryService and MetadataService statuses.
 * @param status - The service status string (case-insensitive)
 * @returns true if the service is in a transitional state
 */
export function isTransitionalState(status: string | undefined | null): boolean {
  if (!status) return false;
  return TRANSITIONAL_STATES.has(status.toLowerCase());
}

/**
 * Get a user-friendly tooltip message for why an action is blocked during a transitional state.
 * @param status - The current service status
 * @returns A tooltip message string, or undefined if not in a transitional state
 */
export function getTransitionalStateTooltip(status: string | undefined | null): string | undefined {
  if (!isTransitionalState(status)) return undefined;
  if (isDeletingState(status)) return 'Cluster is being deleted.';
  const stateLower = status!.toLowerCase();
  return `Cluster is currently ${stateLower}. Please wait for the operation to complete.`;
}

/**
 * Check if a cluster is being deleted. The API reports "Deleting" once a delete is
 * accepted (CO-202); the cluster takes no other action from then on.
 * @param status - The service status string (case-insensitive)
 */
export function isDeletingState(status: string | undefined | null): boolean {
  return status?.toLowerCase() === 'deleting';
}

/**
 * Check if a service status is a terminal failed state.
 * Works for both QueryService and MetadataService statuses.
 * @param status - The service status string (case-insensitive)
 * @returns true if the service is in a failed/error state
 */
export function isFailedState(status: string | undefined | null): boolean {
  if (!status) return false;
  return FAILED_STATES.has(status.toLowerCase());
}

/**
 * Get text color class for query status
 * @param status - The query status string
 * @returns Tailwind CSS classes for text color
 */
export function getStatusTextColor(status: string | unknown): string {
  // Handle non-string inputs
  if (typeof status !== 'string') {
    return 'text-muted-foreground';
  }

  // Trim and uppercase the status
  const statusUpper = status?.trim().toUpperCase();

  switch (statusUpper) {
    case 'SUCCESS':
      return 'text-success';
    case 'FAILED':
    case 'PARSING_FAILED':
      return 'text-destructive';
    case 'CANCELLED':
      return 'text-muted-foreground';
    case 'PLANNING':
    case 'EXECUTING':
      return 'text-info';
    case 'QUEUED':
      return 'text-warning';
    case 'TIMEDOUT':
      return 'text-warning';
    default:
      return 'text-muted-foreground';
  }
}
